/**
 * GSTR-2B Reconciliation Page
 * 
 * Auto-flag discrepancies between purchase register and vendor-filed GSTR-1
 * Per The ERP Architect's Handbook specifications
 */

import { useState, useEffect } from 'react';
import {
  DocumentCheckIcon,
  ExclamationTriangleIcon,
  CheckCircleIcon,
  XCircleIcon,
  ArrowUpTrayIcon,
  ArrowPathIcon,
  FunnelIcon,
  DocumentArrowDownIcon,
  MagnifyingGlassIcon,
} from '@heroicons/react/24/outline';
import { useApp } from '../../context';

interface GSTR2BEntry {
  id: string;
  gstin: string;
  vendor_name: string;
  invoice_number: string;
  invoice_date: string;
  invoice_value: number;
  taxable_value: number;
  igst: number;
  cgst: number;
  sgst: number;
  cess: number;
  itc_available: boolean;
  source: 'gstr2b' | 'books' | 'both';
  match_status: 'matched' | 'mismatched' | 'missing_in_books' | 'missing_in_gstr2b';
  discrepancy_type?: string;
  discrepancy_amount?: number;
}

interface ReconciliationSummary {
  total_gstr2b: number;
  total_books: number;
  matched: number;
  mismatched: number;
  missing_in_books: number;
  missing_in_gstr2b: number;
  itc_as_per_gstr2b: number;
  itc_as_per_books: number;
  itc_difference: number;
}

export default function GSTR2BReconciliation() {
  const { state } = useApp();
  const [selectedPeriod, setSelectedPeriod] = useState('202401');
  const [entries, setEntries] = useState<GSTR2BEntry[]>([]);
  const [filteredEntries, setFilteredEntries] = useState<GSTR2BEntry[]>([]);
  const [summary, setSummary] = useState<ReconciliationSummary | null>(null);
  const [activeFilter, setActiveFilter] = useState<string>('all');
  const [searchQuery, setSearchQuery] = useState('');
  const [showUploadModal, setShowUploadModal] = useState(false);
  const [isReconciling, setIsReconciling] = useState(false);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    loadGSTR2BData();
  }, [selectedPeriod, state.user?.tenant_id]);

  const loadGSTR2BData = async () => {
    if (!state.user?.tenant_id) return;

    try {
      setIsReconciling(true);
      setError(null);

      const result = await window.electronAPI.gst.getGSTR1B2BDetails(state.user.tenant_id, selectedPeriod);
      
      if (result.success && result.data) {
        setEntries(result.data);
        
        // Calculate summary from data
        const matched = result.data.filter((e: GSTR2BEntry) => e.match_status === 'matched').length;
        const mismatched = result.data.filter((e: GSTR2BEntry) => e.match_status === 'mismatched').length;
        const missingInBooks = result.data.filter((e: GSTR2BEntry) => e.match_status === 'missing_in_books').length;
        const missingInGSTR2B = result.data.filter((e: GSTR2BEntry) => e.match_status === 'missing_in_gstr2b').length;
        
        const totalGSTR2B = result.data.filter((e: GSTR2BEntry) => e.source === 'gstr2b' || e.source === 'both').length;
        const totalBooks = result.data.filter((e: GSTR2BEntry) => e.source === 'books' || e.source === 'both').length;
        
        const itcGSTR2B = result.data
          .filter((e: GSTR2BEntry) => (e.source === 'gstr2b' || e.source === 'both') && e.itc_available)
          .reduce((sum: number, e: GSTR2BEntry) => sum + e.igst + e.cgst + e.sgst + e.cess, 0);
        
        const itcBooks = result.data
          .filter((e: GSTR2BEntry) => (e.source === 'books' || e.source === 'both') && e.itc_available)
          .reduce((sum: number, e: GSTR2BEntry) => sum + e.igst + e.cgst + e.sgst + e.cess, 0);
        
        setSummary({
          total_gstr2b: totalGSTR2B,
          total_books: totalBooks,
          matched,
          mismatched,
          missing_in_books: missingInBooks,
          missing_in_gstr2b: missingInGSTR2B,
          itc_as_per_gstr2b: itcGSTR2B,
          itc_as_per_books: itcBooks,
          itc_difference: itcBooks - itcGSTR2B,
        });
      } else {
        setError(result.error || 'Failed to load GSTR-2B reconciliation');
      }
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to load GSTR-2B reconciliation');
    } finally {
      setIsReconciling(false);
    }
  };

  useEffect(() => {
    let filtered = entries;

    if (activeFilter !== 'all') {
      filtered = filtered.filter(e => e.match_status === activeFilter);
    }

    if (searchQuery) {
      filtered = filtered.filter(e =>
        e.vendor_name.toLowerCase().includes(searchQuery.toLowerCase()) ||
        e.gstin.includes(searchQuery) ||
        e.invoice_number.toLowerCase().includes(searchQuery.toLowerCase())
      );
    }

    setFilteredEntries(filtered);
  }, [entries, activeFilter, searchQuery]);

  const formatCurrency = (amount: number) => {
    return new Intl.NumberFormat('en-IN', {
      style: 'currency',
      currency: 'INR',
      maximumFractionDigits: 0,
    }).format(amount);
  };

  const getStatusBadge = (status: string) => {
    const styles: Record<string, { bg: string; text: string; icon: any; label: string }> = {
      matched: { bg: 'bg-green-100', text: 'text-green-700', icon: CheckCircleIcon, label: 'Matched' },
      mismatched: { bg: 'bg-yellow-100', text: 'text-yellow-700', icon: ExclamationTriangleIcon, label: 'Mismatched' },
      missing_in_books: { bg: 'bg-red-100', text: 'text-red-700', icon: XCircleIcon, label: 'Missing in Books' },
      missing_in_gstr2b: { bg: 'bg-orange-100', text: 'text-orange-700', icon: XCircleIcon, label: 'Missing in 2B' },
    };

    const style = styles[status] || styles.matched;
    const Icon = style.icon;

    return (
      <span className={`inline-flex items-center gap-1 px-2 py-1 rounded-full text-xs font-medium ${style.bg} ${style.text}`}>
        <Icon className="h-3 w-3" />
        {style.label}
      </span>
    );
  };

  const handleReconcile = async () => {
    setIsReconciling(true);
    await new Promise(resolve => setTimeout(resolve, 2000));
    setIsReconciling(false);
  };

  const periods = [
    { value: '202401', label: 'January 2024' },
    { value: '202312', label: 'December 2023' },
    { value: '202311', label: 'November 2023' },
    { value: '202310', label: 'October 2023' },
  ];

  return (
    <div className="p-6 bg-gray-50 min-h-screen">
      {/* Header */}
      <div className="flex justify-between items-center mb-6">
        <div>
          <h1 className="text-2xl font-bold text-gray-800">GSTR-2B Reconciliation</h1>
          <p className="text-gray-600">Match purchase register with vendor-filed GSTR-1</p>
        </div>

        <div className="flex gap-3">
          <select
            value={selectedPeriod}
            onChange={(e) => setSelectedPeriod(e.target.value)}
            className="px-4 py-2 border rounded-lg focus:ring-2 focus:ring-blue-500"
          >
            {periods.map(p => (
              <option key={p.value} value={p.value}>{p.label}</option>
            ))}
          </select>

          <button
            onClick={() => setShowUploadModal(true)}
            className="px-4 py-2 bg-white border rounded-lg hover:bg-gray-50 flex items-center gap-2"
          >
            <ArrowUpTrayIcon className="h-5 w-5" />
            Upload GSTR-2B
          </button>

          <button
            onClick={handleReconcile}
            disabled={isReconciling}
            className="px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 flex items-center gap-2 disabled:opacity-50"
          >
            <ArrowPathIcon className={`h-5 w-5 ${isReconciling ? 'animate-spin' : ''}`} />
            Reconcile
          </button>
        </div>
      </div>

      {/* Summary Cards */}
      {summary && (
        <div className="grid grid-cols-6 gap-4 mb-6">
          <div className="bg-white rounded-xl shadow-sm border p-4">
            <p className="text-sm text-gray-500">GSTR-2B Invoices</p>
            <p className="text-2xl font-bold text-gray-800">{summary.total_gstr2b}</p>
          </div>
          <div className="bg-white rounded-xl shadow-sm border p-4">
            <p className="text-sm text-gray-500">Book Invoices</p>
            <p className="text-2xl font-bold text-gray-800">{summary.total_books}</p>
          </div>
          <div className="bg-white rounded-xl shadow-sm border p-4 cursor-pointer hover:bg-green-50" onClick={() => setActiveFilter('matched')}>
            <p className="text-sm text-gray-500">Matched</p>
            <p className="text-2xl font-bold text-green-600">{summary.matched}</p>
          </div>
          <div className="bg-white rounded-xl shadow-sm border p-4 cursor-pointer hover:bg-yellow-50" onClick={() => setActiveFilter('mismatched')}>
            <p className="text-sm text-gray-500">Mismatched</p>
            <p className="text-2xl font-bold text-yellow-600">{summary.mismatched}</p>
          </div>
          <div className="bg-white rounded-xl shadow-sm border p-4 cursor-pointer hover:bg-red-50" onClick={() => setActiveFilter('missing_in_books')}>
            <p className="text-sm text-gray-500">Missing in Books</p>
            <p className="text-2xl font-bold text-red-600">{summary.missing_in_books}</p>
          </div>
          <div className="bg-white rounded-xl shadow-sm border p-4 cursor-pointer hover:bg-orange-50" onClick={() => setActiveFilter('missing_in_gstr2b')}>
            <p className="text-sm text-gray-500">Missing in 2B</p>
            <p className="text-2xl font-bold text-orange-600">{summary.missing_in_gstr2b}</p>
          </div>
        </div>
      )}

      {/* ITC Summary */}
      {summary && (
        <div className="bg-white rounded-xl shadow-sm border p-6 mb-6">
          <h3 className="text-lg font-semibold text-gray-800 mb-4">ITC Summary</h3>
          <div className="grid grid-cols-3 gap-6">
            <div className="text-center p-4 bg-blue-50 rounded-lg">
              <p className="text-sm text-blue-600 mb-1">ITC as per GSTR-2B</p>
              <p className="text-2xl font-bold text-blue-700">{formatCurrency(summary.itc_as_per_gstr2b)}</p>
            </div>
            <div className="text-center p-4 bg-green-50 rounded-lg">
              <p className="text-sm text-green-600 mb-1">ITC as per Books</p>
              <p className="text-2xl font-bold text-green-700">{formatCurrency(summary.itc_as_per_books)}</p>
            </div>
            <div className={`text-center p-4 rounded-lg ${summary.itc_difference > 0 ? 'bg-red-50' : 'bg-gray-50'}`}>
              <p className={`text-sm mb-1 ${summary.itc_difference > 0 ? 'text-red-600' : 'text-gray-600'}`}>
                ITC Difference
              </p>
              <p className={`text-2xl font-bold ${summary.itc_difference > 0 ? 'text-red-700' : 'text-gray-700'}`}>
                {formatCurrency(summary.itc_difference)}
              </p>
              {summary.itc_difference > 0 && (
                <p className="text-xs text-red-500 mt-1">⚠️ Excess ITC claimed in books</p>
              )}
            </div>
          </div>
        </div>
      )}

      {/* Filters & Table */}
      <div className="bg-white rounded-xl shadow-sm border">
        <div className="p-4 border-b flex justify-between items-center">
          <div className="flex gap-2">
            {[
              { value: 'all', label: 'All' },
              { value: 'matched', label: 'Matched' },
              { value: 'mismatched', label: 'Mismatched' },
              { value: 'missing_in_books', label: 'Missing in Books' },
              { value: 'missing_in_gstr2b', label: 'Missing in 2B' },
            ].map(filter => (
              <button
                key={filter.value}
                onClick={() => setActiveFilter(filter.value)}
                className={`px-3 py-1.5 rounded-lg text-sm font-medium transition-colors
                  ${activeFilter === filter.value
                    ? 'bg-blue-600 text-white'
                    : 'bg-gray-100 text-gray-600 hover:bg-gray-200'}`}
              >
                {filter.label}
              </button>
            ))}
          </div>

          <div className="flex gap-2">
            <div className="relative">
              <MagnifyingGlassIcon className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-gray-400" />
              <input
                type="text"
                placeholder="Search vendor, GSTIN, invoice..."
                value={searchQuery}
                onChange={(e) => setSearchQuery(e.target.value)}
                className="pl-9 pr-4 py-2 border rounded-lg text-sm focus:ring-2 focus:ring-blue-500 w-64"
              />
            </div>
            <button className="px-3 py-2 border rounded-lg hover:bg-gray-50">
              <DocumentArrowDownIcon className="h-5 w-5 text-gray-600" />
            </button>
          </div>
        </div>

        <div className="overflow-x-auto">
          <table className="w-full">
            <thead className="bg-gray-50">
              <tr>
                <th className="px-4 py-3 text-left text-xs font-medium text-gray-500">Vendor</th>
                <th className="px-4 py-3 text-left text-xs font-medium text-gray-500">GSTIN</th>
                <th className="px-4 py-3 text-left text-xs font-medium text-gray-500">Invoice</th>
                <th className="px-4 py-3 text-right text-xs font-medium text-gray-500">Taxable Value</th>
                <th className="px-4 py-3 text-right text-xs font-medium text-gray-500">IGST</th>
                <th className="px-4 py-3 text-right text-xs font-medium text-gray-500">CGST</th>
                <th className="px-4 py-3 text-right text-xs font-medium text-gray-500">SGST</th>
                <th className="px-4 py-3 text-center text-xs font-medium text-gray-500">Status</th>
                <th className="px-4 py-3 text-left text-xs font-medium text-gray-500">Discrepancy</th>
              </tr>
            </thead>
            <tbody className="divide-y">
              {filteredEntries.map(entry => (
                <tr key={entry.id} className="hover:bg-gray-50">
                  <td className="px-4 py-3">
                    <div className="font-medium text-gray-800">{entry.vendor_name}</div>
                  </td>
                  <td className="px-4 py-3 font-mono text-sm text-gray-600">{entry.gstin}</td>
                  <td className="px-4 py-3">
                    <div className="text-sm font-medium text-gray-800">{entry.invoice_number}</div>
                    <div className="text-xs text-gray-500">
                      {new Date(entry.invoice_date).toLocaleDateString('en-IN')}
                    </div>
                  </td>
                  <td className="px-4 py-3 text-right text-sm">{formatCurrency(entry.taxable_value)}</td>
                  <td className="px-4 py-3 text-right text-sm">{entry.igst > 0 ? formatCurrency(entry.igst) : '-'}</td>
                  <td className="px-4 py-3 text-right text-sm">{entry.cgst > 0 ? formatCurrency(entry.cgst) : '-'}</td>
                  <td className="px-4 py-3 text-right text-sm">{entry.sgst > 0 ? formatCurrency(entry.sgst) : '-'}</td>
                  <td className="px-4 py-3 text-center">{getStatusBadge(entry.match_status)}</td>
                  <td className="px-4 py-3">
                    {entry.discrepancy_type && (
                      <div>
                        <div className="text-sm text-red-600">{entry.discrepancy_type}</div>
                        <div className="text-xs text-red-500">{formatCurrency(entry.discrepancy_amount || 0)}</div>
                      </div>
                    )}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </div>

      {/* Upload Modal */}
      {showUploadModal && (
        <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50">
          <div className="bg-white rounded-xl w-full max-w-lg p-6">
            <h2 className="text-xl font-bold text-gray-800 mb-4">Upload GSTR-2B JSON</h2>
            
            <div className="border-2 border-dashed border-gray-300 rounded-xl p-8 text-center mb-4">
              <ArrowUpTrayIcon className="h-12 w-12 mx-auto text-gray-400 mb-4" />
              <p className="text-gray-600 mb-2">Drag & drop your GSTR-2B JSON file</p>
              <p className="text-sm text-gray-500">Downloaded from GST Portal</p>
              <button className="mt-4 px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700">
                Choose File
              </button>
            </div>

            <div className="flex justify-end gap-3">
              <button
                onClick={() => setShowUploadModal(false)}
                className="px-4 py-2 border rounded-lg hover:bg-gray-50"
              >
                Cancel
              </button>
              <button className="px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700">
                Upload & Process
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
